import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import type { PnLReport } from '@/types'
import { MONEY_SERIES, SeriesKey, TermLabel, useMoney } from './moneyUi'
import { buildStatement, staffMealsFootnote, type StatementLine } from './pnlStatementLogic'

type Props = {
  summary?: PnLReport['summary']
  periodLabel: string
}

const POS_FEES_HELP =
  'Charges the fiscal device or POS provider takes per invoice. They come off gross sales with the tax, before anything counts as yours.'

/**
 * The statement (F-FE-N164): every rung from gross sales to the result, in
 * order, with the sums closing on screen (F-RPT-03). Beside each figure a bar
 * on one shared scale shows what that line is worth — a total is what is left,
 * a deduction is the slice it removes from the line above — so the reader sees
 * the month's sales being spent without doing the arithmetic.
 */
export function PnlStatement({ summary, periodLabel }: Props) {
  const money = useMoney()
  const { lines, zeroAt } = buildStatement(summary)
  const serviceCharge = summary?.service_charge ?? 0
  const staffMeals = staffMealsFootnote(summary)

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Profit and loss</CardTitle>
        <p className="mt-1 text-xs text-muted-foreground">
          {periodLabel}. Amounts in {money.symbol}; shares are of net sales.
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        {lines.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">No completed sales or recorded expenses in this period.</p>
        ) : (
          <table className="w-full text-sm">
            <caption className="mb-3 caption-top text-left">
              <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <SeriesKey series="sales">What is left</SeriesKey>
                <SeriesKey series="neutral">Tax, fees and refunds</SeriesKey>
                <SeriesKey series="cogs">Cost of goods</SeriesKey>
                <SeriesKey series="opex">Operating expenses</SeriesKey>
              </span>
            </caption>
            <thead className="sr-only">
              <tr>
                <th scope="col">Line</th>
                <th scope="col">Share of gross sales</th>
                <th scope="col">Amount</th>
                <th scope="col">Share of net sales</th>
              </tr>
            </thead>
            <tbody>
              {lines.map(l => (
                <Row key={l.key} line={l} zeroAt={zeroAt} amount={money.plain(l.amount)} />
              ))}
            </tbody>
          </table>
        )}
        {serviceCharge > 0.005 && (
          <p className="mt-4 border-t pt-3 text-xs leading-relaxed text-muted-foreground">
            <TermLabel label="Service charge" metric="service_charge" className="font-medium text-foreground" /> of{' '}
            <span className="tabular-nums text-foreground">{money.full(serviceCharge)}</span> was collected for staff. It is
            not the business&rsquo;s income, so it is not in the statement.
          </p>
        )}
        {staffMeals && (
          <p className="mt-4 border-t pt-3 text-xs leading-relaxed text-muted-foreground">
            <span className="font-medium text-foreground">Staff meals:</span>{' '}
            {staffMeals.meals} {staffMeals.meals === 1 ? 'meal' : 'meals'}, menu value{' '}
            <span className="tabular-nums text-foreground">{money.full(staffMeals.menuValue)}</span>. They are not sales;
            the line above is their ingredient cost.
            {staffMeals.noRecipe > 0.005 && (
              <>
                {' '}
                <span className="tabular-nums text-foreground">{money.full(staffMeals.noRecipe)}</span> of it is not costed
                because those dishes have no recipe.
              </>
            )}
            {staffMeals.inPurchases > 0.005 && (
              <>
                {' '}
                <span className="tabular-nums text-foreground">{money.full(staffMeals.inPurchases)}</span> was eaten on days
                when cost of goods is what you bought, so its food is already counted in purchases.
              </>
            )}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function Row({ line, zeroAt, amount }: { line: StatementLine; zeroAt: number; amount: string }) {
  const isTotal = line.kind === 'total'
  const isLoss = isTotal && line.amount < -0.005
  return (
    <tr className={cn(isTotal && line.key !== 'gross_sales' && 'border-t')}>
      <th
        scope="row"
        className={cn(
          'py-2 pr-3 text-left font-normal leading-snug sm:whitespace-nowrap sm:pr-4',
          isTotal ? 'font-medium text-foreground' : 'pl-4 text-muted-foreground',
          line.isResult && 'font-semibold',
        )}
      >
        <TermLabel
          label={line.label}
          metric={line.metric}
          help={line.key === 'pos_fees' ? POS_FEES_HELP : undefined}
        />
      </th>
      <td className="hidden w-full py-2 pr-4 sm:table-cell" aria-hidden>
        <div className="relative h-4">
          {zeroAt > 0.5 && <span className="absolute inset-y-[-4px] w-px bg-border" style={{ left: `${zeroAt}%` }} />}
          <span
            className={cn('absolute top-1/2 -translate-y-1/2 rounded-[2px]', isTotal ? 'h-3' : 'h-1.5')}
            style={{
              left: `${line.barLeft}%`,
              width: `max(2px, ${line.barWidth}%)`,
              background: MONEY_SERIES[line.tone],
            }}
          />
        </div>
      </td>
      <td
        className={cn(
          'whitespace-nowrap py-2 pl-2 text-right tabular-nums',
          isTotal ? 'font-medium text-foreground' : 'text-muted-foreground',
          isLoss && 'text-danger-fg',
          line.isResult && 'font-semibold',
        )}
      >
        {/* The accountant's double rule under the figure the statement closes on. */}
        <span className={cn(line.isResult && 'border-b-[3px] border-double border-current pb-0.5')}>{amount}</span>
      </td>
      <td className="w-12 whitespace-nowrap py-2 pl-2 text-right text-xs tabular-nums text-muted-foreground sm:w-16 sm:pl-4">
        {line.pctOfNetSales == null ? '' : `${line.pctOfNetSales.toFixed(1)}%`}
      </td>
    </tr>
  )
}
